<template>
    <el-dialog
        class="engine-choice-modal"
        :title="`${getText('audit_report_config_modal_title')} ${$route.params.modelName}`"
        :visible.sync="showModal"
        width="80%"
        :show-close="false"
        :close-on-press-escape="false"
        :close-on-click-modal="false"
    >
        <div>
            <div class="max-gain-container">
                <p class="max-gain-label">{{getText('audit_report_config_max_gain_label')}}</p>
                <el-slider tooltip-class="max-gain-slider-tooltip" class="max-gain-slider" :value="securityCoeff" :format-tooltip="formatTooltip" @input="emitNewSecurityCoeff"></el-slider>
            </div>
            <p class="max-gain-description">{{getText('audit_report_config_max_gain_description')}}</p>
            <p class="max-gain-override-values-description">{{getText('audit_report_config_max_gain_override_values_description')}}</p>
        </div>
        <div v-loading="loadingTable">
            <div style="text-align: left;">
                <p class="engine-choice-modal-label">{{ getText('audit_report_config_modal_engines_select_label') }}</p>
                <el-button class="greenButton rank-button" @click="setRanks">{{ getText('audit_report_solutions_ranks_button') }}</el-button>
                <p class="rank-button-info">{{ getText('audit_report_solutions_ranks_button_info') }}</p>
            </div>

            <el-table
                v-if="orderedDataForTable.length"
                :data="orderedDataForTable"
                :row-class-name="rowClassName"
                :default-sort="defaultSort"
                height="400"
                ref="data"
                @sort-change="sortData"
            >
                <el-table-column
                    prop="name"
                    min-width="200"
                    sortable
                    :label="getText('audit_report_config_modal_engines_table_name')"
                ></el-table-column>
                <el-table-column
                    prop="solution"
                    sortable
                    min-width="200"
                    :label="getText('audit_report_config_modal_engines_table_solution')"
                ></el-table-column>
                <el-table-column
                    sortable="custom"
                    min-width="120"
                    prop="tri"
                    :label="getText('audit_report_config_modal_engines_table_tri')"
                >
                    <template slot-scope="scope">
                        <tri-row :scope="scope" :neg-val="negVal" :function-calc-r-o-i-coef="solutionROIWithNewCoef"></tri-row>
                    </template>
                </el-table-column>
                <el-table-column
                    sortable
                    :sort-method="sortEnergyGain"
                    min-width="120"
                    :label="getText('audit_report_config_modal_engines_table_gain_energy')"
                >
                    <template slot-scope="scope">
                        <div v-if="scope.row.overridden === false" class="value" :class="{'red': negVal(applyMaxSecurityCoeff(scope.row.gainMWh))}">{{ applyMaxSecurityCoeff(scope.row.gainMWh) }} MWh</div>
                        <div v-else class="value" :class="{'red': negVal(scope.row.gainMWh)}">{{ scope.row.gainMWh }} MWh</div>
                    </template>
                </el-table-column>
                <el-table-column
                    :sort-method="sortMoneyGain"
                    sortable
                    :label="getText('audit_report_config_modal_engines_table_gain_money')"
                    min-width="120"
                >
                    <template slot-scope="scope">
                        <div v-if="scope.row.overridden === false" class="value" :class="{'red': negVal(applyMaxSecurityCoeff(scope.row.gain_money))}">{{ applyMaxSecurityCoeff(scope.row.gain_money) }}</div>
                        <div v-else class="value" :class="{'red': negVal(scope.row.gain_money)}">{{ scope.row.gain_money }}</div>
                    </template>
                </el-table-column>
                <el-table-column
                    sortable
                    :sort-method="sortRawInvest"
                    prop="investissement"
                    min-width="150"
                    :label="getText('audit_report_config_modal_engines_table_raw_invest')"
                ></el-table-column>
                <el-table-column
                    v-if="enableCEE"
                    sortable
                    :sort-method="sortGlobalInvest"
                    prop="investissement_global"
                    min-width="150"
                    :label="getText('audit_report_config_modal_engines_table_overall_invest')"
                ></el-table-column>
                <el-table-column
                    min-width="80"
                    fixed="right"
                    prop="rank"
                    sortable
                    :sort-method="sortRank"
                >
                    <template slot="header">
                        <div>{{ getText('audit_report_config_modal_engines_table_rang') }}</div>
                    </template>
                    <template slot-scope="scope">
                        <el-input v-model="scope.row.rank" type="number" @input="checkNegativeRankValue"></el-input>
                    </template>
                </el-table-column>
                <el-table-column
                    fixed="right"
                    align="center"
                    min-width="120"
                    :label="getText('audit_report_config_modal_engines_table_select')"
                >
                    <template slot-scope="scope">
                        <el-checkbox v-model="scope.row.selected"></el-checkbox>
                    </template>
                </el-table-column>
            </el-table>
            <div v-else>{{ getText('audit_report_config_modal_engines_no_data') }}</div>
        </div>
        <span slot="footer">
            <el-button class="whiteButton" @click="cancelAction">{{ modifySelection ? getText('cancel_button') : getText('audit_report_config_modal_return_button') }}</el-button>
            <el-tooltip popper-class="override-button-tooltip" :visible-arrow="false" effect="dark" :disabled="isNewReport === false" :content="getText('audit_report_config_modal_override_button_tooltip')" placement="top">
                <el-button class="greenButton" :class="isNewReport === true ? 'disabled-button' : ''" @click="isNewReport === true ? () => {} : openOverrideModal()">{{ getText('audit_report_config_modal_override_button') }}</el-button>
            </el-tooltip>
            <el-button class="greenButton" @click="emitSelection">{{ getText('audit_report_config_modal_validate_button') }}</el-button>
        </span>
        <modale-value-override
            ref="modaleValueOverride"
            :overriddenAuditData="overriddenAuditData"
            :defaultAuditData="defaultAuditData"
            @saveNewValue="saveNewValue"
            @sendOverriddenValues="generateDataForTable"
        ></modale-value-override>
    </el-dialog>
</template>

<script>

import { mapGetters } from 'vuex';
import modaleValueOverride from './modaleValueOverride.vue'
import {cloneDeep} from 'lodash';
import TriRow from './ModalChooseEngineTriRow.vue';

export default {
    components: {
        modaleValueOverride,
        TriRow,
    },
    props: {
        modifySelection: {
            type: Boolean,
            required: true
        },
        securityCoeff: {
            type: Number,
            required: true
        },
        config: {
            type: Array,
            required: true
        },
        existingOverriddenAuditData: {
            type: Object,
            required: true
        },
        defaultAuditData: {
            type: Object,
            required: true
        },
        enableCEE: {
            type: Boolean,
            required: true
        }
    },

    data: function(){
        return {
            isNewReport: false,
            showModal: false,
            simulationForReport: [],
            dataForTable: [],
            orderedDataForTable: [],
            previousRank: {},
            loadingTable: false,
            overriddenAuditData: {}
        };
    },

    computed: {
        ...mapGetters([
            'getText',
            'getFactoryEngines',
            'factoryEnginesByUid',
            'getLangage',
            'modelReports'
        ]),
        defaultSort() {
            let defaultSort = {
                prop: 'tri',
                order: 'ascending'
            }
            if (this.updatingConfig) {
                defaultSort = {
                    prop: 'rank',
                    order: 'ascending'
                }
            }
            return defaultSort
        },
        updatingConfig() {
            return this.config.length > 0;
        },

        auditEngines() {
            return this.getFactoryEngines(this.$route.params.auditId).filter(engine => !engine.archived).map(engine => engine.uid)
        },
        chosenSolutions() {
            return this.dataForTable.filter(data => data.selected)
        },
    },

    mounted: function() {
    },

    methods: {
        saveNewValue(payload) {
            this.overriddenAuditData[payload.motor].solutions_table[payload.solutionIndex][payload.overridableKey] = payload.value;
        },
        setOverriddenAuditData() {
            let overriddenAuditData = {};
            this.chosenSolutions.forEach(solution => {
                if (overriddenAuditData[solution.name] === undefined) {
                    overriddenAuditData[solution.name] = {
                        solutions_table: []
                    }
                }
                if (
                    this.existingOverriddenAuditData[solution.name] !== undefined
                    && this.existingOverriddenAuditData[solution.name].solutions_table.find(data => solution.solution_not_formatted === data.name.toUpperCase())
                ) {
                    overriddenAuditData[solution.name].solutions_table.push(this.existingOverriddenAuditData[solution.name].solutions_table.find(data => solution.solution_not_formatted === data.name))
                } else {
                    let data = this.defaultAuditData[solution.name].solutions_table.find(data => solution.solution_not_formatted === data.name)
                    if (data) {
                        overriddenAuditData[solution.name].solutions_table.push(data)
                    }
                }
            })
            this.$set(this, 'overriddenAuditData', overriddenAuditData);
        },
        openOverrideModal() {
            this.setOverriddenAuditData();
            this.$refs.modaleValueOverride.open();
        },
        formatTooltip(val) {
            return `${val} %`;
        },
        solutionROIWithNewCoef(solution) {
            let solutionCost = this.solutionYearlyCost(solution)
            let invest = this.parseToFloat(solution.investissement);
            let gain = this.parseToFloat(solution.gain_money) * (1 - (this.securityCoeff / 100));
            if (gain - solutionCost === 0) {
                return 0;
            }
            return this.limitDecimal(invest / (gain - solutionCost));
        },
        limitDecimal(value) {
            return Math.round(value * 100) / 100;
        },
        parseToFloat(value) {
            if (typeof value === "number") {
                return value;
            }
            return parseFloat(value.replace(/[,]/, '.').replace(/[\s]/, ''))
        },
        solutionYearlyCost(solution) {
            let invest = this.parseToFloat(solution.investissement);
            let roi = this.parseToFloat(solution.tri);
            let gain = this.parseToFloat(solution.gain_money);
            return gain - invest / roi;
        },
        applyMaxSecurityCoeff(value) {
            return this.limitDecimal(this.parseToFloat(value) * (1 - (this.securityCoeff / 100)));
        },
        emitNewSecurityCoeff(value) {
            if (!value || (parseFloat(value) <= 100 && parseFloat(value) >= 0)) {
                this.$emit('saveNewSecurityCoeff', value);
            } else {
                this.$notify.error({
                    message: this.getText('audit_report_config_max_gain_error_message')
                })
            }
        },
        async getData() {
            try{
              let response = await this.$store.dispatch('listModels', {
                  url: 'simulation/',
                  filters: {
                    c_resp_page_size: 8,
                    archived: false,
                    is_latest: true,
                    c_resp_nested: false,
                    motor__in: this.auditEngines.join(','),
                  }
              });
              this.simulationForReport = response.results;
              this.generateDataForTable();
            }
            catch(error){
              this.simulationForReport = [];
              console.error(error);
            }
            finally {
              this.loadingTable = false;
            }
        },
        setRanks() {
            let counter = 1;
            this.$refs.data.tableData.map(data => {
                if (data.selected) {
                    data.rank = counter;
                    counter++;
                } else {
                    data.rank = 0;
                }
            })
        },
        checkNegativeRankValue() {
            this.dataForTable.forEach(data => {
                if (parseInt(data.rank) < 0) {
                    data.rank = 0;
                }
            });
        },
        sortRawInvest(first, second) {
            return parseFloat(first.investissement.replace(/[,]/, '.').replace(/[\s]/, '')) - parseFloat(second.investissement.replace(/[,]/, '.').replace(/[\s]/, ''))
        },
        sortGlobalInvest(first, second) {
            return parseFloat(first.investissement_global.replace(/[,]/, '.').replace(/[\s]/, '')) - parseFloat(second.investissement_global.replace(/[,]/, '.').replace(/[\s]/, ''))
        },
        sortRank(first, second) {
            if (parseInt(first.rank) === 0) {
                return 1
            }
            return parseInt(first.rank) - parseInt(second.rank);
        },
        sortData (params){
            if(params.prop === 'tri'){
                if(params.order === null) {
                    this.$set(this, 'orderedDataForTable', this.dataForTable);
                }
                let data = this.dataForTableTriNonSNR();
                data.push(...this.dataForTableTriSNR());

                if(params.order === 'descending') {
                    data = data.reverse();
                }
                this.$set(this, 'orderedDataForTable', data);
            }else{
                this.$set(this, 'orderedDataForTable', this.dataForTable);
            }
        },
        dataForTableTriNonSNR (){
            return this.dataForTable.filter((data) => data.tri > 0).sort((a,b) => a.tri - b.tri);
        },
        dataForTableTriSNR (){
            return this.dataForTable.filter((data) => data.tri < 0);
        },
        sortTRI(first, second) {
            return this.parseToFloat(first.tri) - this.parseToFloat(second.tri)
        },
        sortEnergyGain(first, second) {
            return this.parseToFloat(first.gainMWh) - this.parseToFloat(second.gainMWh)
        },
        sortMoneyGain(first, second) {
            return this.parseToFloat(first.gain_money) - this.parseToFloat(second.gain_money)
        },
        cancelAction() {
            if (this.modifySelection) {
                this.showModal = false;
            } else {
                this.$router.push({
                    name: 'moteurs',
                    params: {
                        siteid: this.$route.params.auditId
                    }
                })
            }
        },
        negVal(val) {
            return parseFloat(val) < 0
        },
        rowClassName(data) {
            let classes = [];
            if (data.row.selected === true) {
                classes.push('row-selected');
            }
            if (data.row.overridden === true) {
                classes.push('overridden-value');
            }

            return classes.join(' ');
        },
        open(newReport=false) {
            this.$set(this, 'overriddenAuditData', this.existingOverriddenAuditData);
            this.loadingTable = true;
            this.getData();
            this.showModal = true;
            this.isNewReport = newReport;
        },
        chosenSolutionsAreRanked() {
            let ranks = {}
            let rankError = false
            this.chosenSolutions.forEach(solution => {
                let solutionRank = parseInt(solution.rank)
                if (ranks[solutionRank] === undefined && solutionRank > 0) {
                    ranks[solutionRank] = solution.solution
                }
                else {
                    rankError = true;
                }
            })
            return rankError;
        },
        emitSelection() {
            if (this.chosenSolutionsAreRanked() === false) {
                this.showModal = false;
                this.$emit('sendChosenEngines', {
                    solutions: this.chosenSolutions,
                    overriddenAuditData: this.overriddenAuditData
                })
            }
            else {
                this.$notify.error({
                    message: this.getText('audit_report_error_ranking_engines')
                })
            }
        },
        getMotorClassTranslation(opt) {
            return this.$store.state.trad[opt] ? this.$store.state.trad[opt][this.getLangage] : `${opt.toUpperCase()}`;
        },
        generateDataForTable() {
            this.$set(this, 'dataForTable', []);
            this.simulationForReport.forEach(simulation => {
                let motor = this.factoryEnginesByUid(simulation.motor_uid);
                if (simulation.costs.length) {
                    simulation.costs.forEach((entry, index) => {
                        // Exclude solutions that have VEV if the current motor already has VEV
                        if (motor.regulation_type === 'electronic_speed_variation' && entry.has_variator){
                            return;
                        }
                        let option = entry.has_variator && entry.switch_motor !== 'current' ? `${this.getMotorClassTranslation('+ variator')}`: '';
                        let solution = `${this.getMotorClassTranslation(entry.switch_motor)}${option}`;
                        let rank = 0;
                        let selected = false;
                        if (this.config.length) {
                            let previousConfig = this.config.find(config_item => {
                                return config_item.moteur === simulation.motor_uid && config_item.solution === solution
                            })
                            if (previousConfig) {
                                selected = true;
                                rank = previousConfig.rank;
                            }
                        }
                        let overridden = false;
                        let gainMWh = simulation.gains_kwh[index].gain_kwh / 1000;
                        let gain_money = simulation.gains_euros[index].gain_euros;
                        let investissement = simulation.costs[index].cost;
                        let investissement_global = simulation.costs[index].cost - (simulation.cee[index].subvention_cee || 0);
                        let tri = simulation.times_before_benefit[index].time_before_benefit;
                        let overriddenAuditData = null;
                        if (this.overriddenAuditData[motor.name]) {
                            overriddenAuditData = this.overriddenAuditData[motor.name].solutions_table.find(solution => {
                                return solution.name === `${entry.switch_motor}${entry.has_variator ? ' + VEV' : ''}`.toUpperCase();
                            })
                        }
                        if (overriddenAuditData) {
                            let defaultAuditData = this.defaultAuditData[motor.name].solutions_table.find(solution => {
                                return solution.name === `${entry.switch_motor}${entry.has_variator ? ' + VEV' : ''}`.toUpperCase();
                            })
                            Object.keys(overriddenAuditData).forEach(key => {
                                if (
                                    !['name', 'best_engine', 'is_selected_solution'].includes(key)
                                    && parseFloat(overriddenAuditData[key]) !== defaultAuditData[key]
                                ) {
                                    overridden = true;
                                }
                            })
                        }

                        this.dataForTable.push({
                            overridden: overridden,
                            motor_uid: simulation.motor_uid,
                            name: motor.name,
                            solution: solution,
                            solution_not_formatted: `${entry.switch_motor}${entry.has_variator ? ' + VEV' : ''}`.toUpperCase(),
                            gainMWh: overriddenAuditData ? overriddenAuditData.gains_mwh : gainMWh,
                            gain_money: overriddenAuditData ? overriddenAuditData.gains_k_an : gain_money,
                            investissement: new Intl.NumberFormat(
                                'fr-FR',
                                {
                                    style: 'currency',
                                    currency: 'EUR'
                                }
                            ).format(overriddenAuditData ? overriddenAuditData.invest : investissement),
                            investissement_global: new Intl.NumberFormat(
                                'fr-FR',
                                {
                                    style: 'currency',
                                    currency: 'EUR'
                                }
                            ).format(overriddenAuditData ? overriddenAuditData.invest : investissement_global),
                            tri: overriddenAuditData ? overriddenAuditData.roi : tri,
                            rank: rank, //(pas ds le model, à calculer a l'avance ?),
                            selected: selected
                        })
                    })
                }
            });
            this.sortData({prop: 'tri', order: 'ascending'});
        }
    },
};
</script>

<style lang="scss">
.engine-choice-modal {
    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
}
.override-button-tooltip {
    background: #c4d600 !important;
    width: 300px;
    font-size: 13px;
}
.tooltip-text {
    display: flex;
    i {
        margin-left: 5px;
    }
}
.max-gain-slider-tooltip {
    width: 60px;
    text-align: center;
}
.engine-choice-modal {
    text-align: left;
    .max-gain-container {
        display: flex;
        align-items: center;
        .max-gain-label {
            margin-right: 20px;
            margin-bottom: 0;
        }
        .max-gain-slider {
            width: 300px;
            .el-slider__bar {
                background-color: #C4D600;
            }
            .el-slider__button {
                border-color: #C4D600;
            }
        }
    }
    .max-gain-description, .max-gain-override-values-description {
        width: 100%;
        font-size: 10px;
        font-style: italic;
        margin-bottom: 20px;
        text-align: left;
    }
    .disabled-button {
        background-color: #dee873 !important;
        cursor: not-allowed;
    }
    .model-type-container {
        text-align: left;
        margin-bottom: 30px;
        .el-select {
            margin: 0!important
        }
    }
    .el-table {
        .red {
            color: #E66453;
            -webkit-text-stroke: 0.3px;
        }
    }
    .row-selected {
        background-color: #F9FBE7;
        &:hover>td, .hover-row>td {
            background-color: #F4F7D0 !important;
        }
    }
    .overridden-value {
        td {
            font-weight: bold;
        }
    }
    .el-table__body tr.hover-row>td {
        background-color: #F4F7D0 !important;
    }
    .el-checkbox__input.is-checked .el-checkbox__inner, .el-checkbox__input.is-indeterminate .el-checkbox__inner {
        background-color: #C4D600;
        border-color: #C4D600;
    }
    .el-checkbox__input.is-focus .el-checkbox__inner, .el-checkbox__inner:hover, .el-select .el-input.is-focus .el-input__inner, .el-select .el-input__inner:focus {
        border-color: #C4D600;
    }
    .dialog-header {
        margin-bottom: 30px;
        .engine-choice-modal-label {
            text-align: left;
            font-size: 20px;
            margin-bottom: 20px;
        }
        .rank-button {
            margin-right: 20px;
        }
        .rank-button-info {
        }
    }

    .cee-activation-section {
        font-size: 20px;
        display: flex;
        align-items: center;
        .el-switch__label {
            span {
                font-weight: 300 !important;
                font-size: 20px !important;
            }
            &:not(.is-active) {
                color: #606266 !important;
            }
            &.is-active {
                color: #C4D600 !important;
            }
        }

    }
}
.el-select-dropdown__item.selected {
    color: #C4D600;
}
</style>



// WEBPACK FOOTER //
// src/components/page/report/modaleChooseEngines.vue